import { appCache, ICache } from '../cache/cache';
import {
  CohortMetricId,
  EmployeeCohortMetricId,
  GroupingInput,
  RegularMetricId,
  TimeSelectionInput,
} from '../graphql/generated/graphql-sdk';
import { Hasher } from '../v2/api/hasher';
import { hashString, Role } from '../v2/api/types';
import { Domains } from '../v2/constants';
import {
  ApiMasterDataAdvancedQueryDimension,
  ApiMasterDataMovementQuery,
  ApiMasterDataQueryMeasure,
  ApiMasterDataQueryResponse,
  ApiMetricQueryResponse,
} from './api-interfaces';
import { DataService } from './data-service';
import {
  NewBackendMasterDataApiService,
  SQLFilters,
} from './master-data-service';

export class CachedMasterDataService implements DataService {
  constructor(
    readonly masterDataApiService: NewBackendMasterDataApiService,
    readonly domain: Domains,
    readonly executorRole: Role,
    readonly simulatedRole: Role | null,
    readonly cache: ICache = appCache
  ) {}

  public getDeps = () => [
    hashString([
      ...this.masterDataApiService.getDeps(),
      this.domain,
      Hasher.hash(this.executorRole),
      Hasher.hash(this.simulatedRole),
    ]),
  ];

  public executeCustomSqlQuery = (
    dimensions: ApiMasterDataAdvancedQueryDimension[],
    measures: ApiMasterDataQueryMeasure[],
    query: string,
    disableNestLoop?: boolean
  ): Promise<ApiMasterDataQueryResponse> => {
    const cacheKey = JSON.stringify([
      'executeCustomSqlQuery',
      this.domain,
      hashString([
        Hasher.hash(this.executorRole),
        Hasher.hash(this.simulatedRole),
      ]),
      query,
    ]);
    return this.cache.getFromCacheOrRequest<ApiMasterDataQueryResponse>(
      cacheKey,
      () =>
        this.masterDataApiService.executeCustomSqlQuery(
          this.domain,
          dimensions,
          measures,
          query,
          disableNestLoop
        )
    );
  };

  public executeMovementQuery = (
    query: ApiMasterDataMovementQuery
  ): Promise<ApiMasterDataQueryResponse> => {
    const cacheKey = JSON.stringify([
      'executeMovementQuery',
      this.domain,
      hashString([
        Hasher.hash(this.executorRole),
        Hasher.hash(this.simulatedRole),
      ]),
      query,
    ]);
    return this.cache.getFromCacheOrRequest<ApiMasterDataQueryResponse>(
      cacheKey,
      () => this.masterDataApiService.executeMovementQuery(this.domain, query)
    );
  };

  public queryCohortMetrics = (
    timeSelection: TimeSelectionInput,
    metrics: CohortMetricId[],
    userCohortFilters?: SQLFilters | null,
    grouping?: GroupingInput,
    disableNestLoop?: boolean
  ): Promise<ApiMetricQueryResponse> => {
    const cacheKey = JSON.stringify([
      'queryCohortMetrics',
      this.domain,
      this.executorRole,
      this.simulatedRole,
      timeSelection,
      metrics,
      userCohortFilters,
      grouping,
      disableNestLoop,
    ]);
    return this.cache.getFromCacheOrRequest<ApiMetricQueryResponse>(
      cacheKey,
      () =>
        this.masterDataApiService.queryCohortMetrics(
          this.domain,
          timeSelection,
          metrics,
          userCohortFilters,
          grouping,
          disableNestLoop
        )
    );
  };

  public queryEmployeeCohortMetrics = (
    timeSelection: TimeSelectionInput,
    metrics: EmployeeCohortMetricId[],
    userFilters?: SQLFilters | null,
    userCohortFilters?: SQLFilters | null,
    grouping?: GroupingInput,
    disableNestLoop?: boolean
  ): Promise<ApiMetricQueryResponse> => {
    const cacheKey = JSON.stringify([
      'queryEmployeeCohortMetrics',
      this.domain,
      this.executorRole,
      this.simulatedRole,
      timeSelection,
      metrics,
      userFilters,
      userCohortFilters,
      grouping,
      disableNestLoop,
    ]);
    return this.cache.getFromCacheOrRequest<ApiMetricQueryResponse>(
      cacheKey,
      () =>
        this.masterDataApiService.queryEmployeeCohortMetrics(
          this.domain,
          timeSelection,
          metrics,
          userFilters,
          userCohortFilters,
          grouping,
          disableNestLoop
        )
    );
  };

  public queryMetrics = (
    timeSelection: TimeSelectionInput,
    metrics: RegularMetricId[],
    userFilters?: SQLFilters | null,
    grouping?: GroupingInput,
    disableNestLoop?: boolean
  ): Promise<ApiMetricQueryResponse> => {
    const cacheKey = JSON.stringify([
      'queryMetrics',
      this.domain,
      hashString([
        Hasher.hash(this.executorRole),
        Hasher.hash(this.simulatedRole),
      ]),
      timeSelection,
      metrics,
      userFilters,
      grouping,
      disableNestLoop,
    ]);
    return this.cache.getFromCacheOrRequest<ApiMetricQueryResponse>(
      cacheKey,
      () =>
        this.masterDataApiService.queryMetrics(
          this.domain,
          timeSelection,
          metrics,
          userFilters,
          grouping,
          disableNestLoop
        )
    );
  };
}
